import { QueryClientProvider, dehydrate, hydrate, type DehydratedState, type QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';

import { RouteError } from './components/route-error';
import { AuthProvider } from './lib/auth';
import { createQueryClient } from './lib/query-client';
// Generated by @tanstack/router-plugin from src/routes on first dev/build run.
// (gitignored; do not edit by hand.)
import { routeTree } from './routeTree.gen';

export interface RouterContext {
  queryClient: QueryClient;
  /**
   * 목록 상태 쿠키(zx_list)를 읽는 곳 — 서버는 요청의 Cookie 헤더, 브라우저는 document.cookie.
   * 라우트는 document 를 직접 만지지 않는다. 브라우저는 이동마다 새로 읽어야 하므로 값이 아니라 함수다.
   */
  readCookie: () => string | null;
}

/**
 * 라우터 팩토리 — 서버는 요청마다, 브라우저는 한 번 만든다 (docs/SSR.md).
 *
 * QueryClient 도 라우터마다 새로 만들어 context 로 넘긴다. loader 는
 * `context.queryClient.ensureQueryData` 로 캐시를 채우고, `dehydrate` 가 그 캐시를
 * 라우터 상태에 실어 보내면 브라우저의 `hydrate` 가 같은 캐시를 복원한다.
 */
export function createAppRouter(readCookie: RouterContext['readCookie'] = () => document.cookie) {
  const queryClient = createQueryClient();

  return createRouter({
    routeTree,
    context: { queryClient, readCookie } satisfies RouterContext,
    scrollRestoration: true,
    // loader 가 던지면(API 장애) 그 라우트 자리에 오류 화면 — 레이아웃은 남는다. 서버 응답은 500.
    defaultErrorComponent: RouteError,
    dehydrate: () =>
      ({ queryClientState: dehydrate(queryClient) }) as unknown as { queryClientState: string },
    hydrate: (dehydrated) => {
      hydrate(queryClient, dehydrated.queryClientState as unknown as DehydratedState);
    },
    // Wrap 은 문서(<html>) 바깥을 감싼다 — DOM 을 그리지 않는 공급자만 둔다.
    // SnackbarProvider 처럼 요소를 그리는 것은 __root 의 RootLayout 안에 있다.
    Wrap: ({ children }) => (
      <QueryClientProvider client={queryClient}>
        <AuthProvider>{children}</AuthProvider>
      </QueryClientProvider>
    ),
  });
}

// Register the router type for full type-safety across the app.
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
